import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { SIZE, VARIANT } from '~/components/isolated/IconButton/constants';
import IconButton from '~/components/isolated/IconButton/IconButton';
import { SimpleInput } from '~/components/isolated/SimpleInput';
import { VARIANT as TYPOGRAPHY_VARIANT } from '~/components/isolated/Typography/constants';
import Typography from '~/components/isolated/Typography/Typography';
import { usePromocode } from '~/hooks/usePromocode';

import { cartStore } from '~/stores';

import styles from './PromoCodeInput.module.scss';

/**
 * PromoCodeInput component
 * Input field with apply button for promo codes
 */
const PromoCodeInput = observer(() => {
  const { t } = useTranslation();

  const {
    promocodeVal,
    isLoading,
    handlePromocodeChange,
    handleDelPromocode,
    handleSubmit,
    checkPromocode,
    promocodeExists,
  } = usePromocode({
    checkPercentageType: true,
    watchPaidWithDiscount: true,
  });

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      checkPromocode(promocodeVal);
    }
  };

  const isCartCalculating = cartStore.calculationProcess.isLoading;
  const hasValue = promocodeVal.trim().length > 0;

  return (
    <div className={styles.wrapper}>
      <form className={styles.container} onSubmit={handleSubmit}>
        <SimpleInput
          type="text"
          value={promocodeVal}
          onChange={handlePromocodeChange}
          onKeyDown={handleKeyDown}
          placeholder={t('promocode')}
          disabled={isLoading || isCartCalculating}
          className={styles.input}
          clearable={hasValue && !promocodeExists}
          onClear={handleDelPromocode}
        />

        <div className={styles.buttonWrapper}>
          <IconButton
            type={promocodeExists ? 'button' : 'submit'}
            onClick={promocodeExists ? handleDelPromocode : undefined}
            disabled={
              promocodeExists
                ? isLoading || isCartCalculating
                : !hasValue || isLoading || isCartCalculating
            }
            variant={
              hasValue && !promocodeExists ? VARIANT.primary : VARIANT.outline
            }
            size={SIZE.L}
            icon={promocodeExists ? 'mark-3.1' : 'arroy-right-3.1'}
            className={classes([])}
          />
        </div>
      </form>

      {!isLoading && cartStore.promocode.message && (
        <Typography
          variant={TYPOGRAPHY_VARIANT['Text 200/Regular']}
          className={classes([
            styles.message,
            cartStore.promocode.errorType === 'error' && styles.error,
            cartStore.promocode.errorType === 'warning' && styles.warning,
            cartStore.promocode.success === true && styles.success,
          ])}
        >
          {cartStore.promocode.message}
        </Typography>
      )}
    </div>
  );
});

export default PromoCodeInput;
